import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Box,
  Container,
  Paper,
  Fade,
  Grow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Menu,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import { 
  Dashboard as DashboardIcon, 
  Videocam as VideocamIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getStoredToken,
  authenticatedFetch,
  getROICameras,
  getCameraStreams,
} from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { addDays, format } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import AnalyticsGraphCard from './graph';

import PieChartCard from './PieChartCard';
import AttendanceSummaryCard from './AttendanceSummaryCard';
import RecognitionMatchGraphCard from './RecognitionMatchGraphCard';
import UniqueCheckinCheckoutCard from './UniqueCheckinCheckoutCard';
import PremisesStatsCard from './PremisesStatsCard';
import LineChartCard from './lineChart';
import { DashboardWebSocketProvider } from '../contexts/DashboardWebSocketContext';

const WEBVIEW_BTN_SX = {
  background: '#ffffff',
  color: '#1f2937',
  border: '1px solid rgba(64, 81, 138, 0.12)',
  fontWeight: 600,
  textTransform: 'none',
  minWidth: { xs: 200, sm: 230 },
  minHeight: { xs: 42, sm: 48 },
  px: { xs: 2, sm: 3 },
  py: { xs: 1, sm: 1.5 },
  boxShadow: '0 10px 28px rgba(31, 45, 84, 0.12)',
  '&:hover': {
    background: '#ffffff',
    borderColor: 'rgba(64, 81, 138, 0.22)',
    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
    color: '#111827',
  },
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    color: '#40518A',
  },
  transition: 'all 0.2s ease',
};

const emptyCustomForm = () => ({
  name: '',
  webview_type: 'attendance',
  inCameras: [],
  outCameras: [],
});

const cameraOptionLabel = (camera) =>
  camera?.label || camera?.camera_label || camera?.id || 'Camera';

const Dashboard = () => {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const intrusionWebviewAllowed = Array.isArray(permissions?.allowed_routes)
    && permissions.allowed_routes.includes('/webview-intrusion');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [dateRange, setDateRange] = useState([
    {
      startDate: addDays(new Date(), -7),
      endDate: new Date(),
      key: 'selection'
    }
  ]);
  const [customWebviews, setCustomWebviews] = useState([]);
  const [inCameraOptions, setInCameraOptions] = useState([]);
  const [outCameraOptions, setOutCameraOptions] = useState([]);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customForm, setCustomForm] = useState(emptyCustomForm);
  const [editingWebview, setEditingWebview] = useState(null);
  const [customSaving, setCustomSaving] = useState(false);
  const [customError, setCustomError] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuWebview, setMenuWebview] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const visibleCustomWebviews = useMemo(
    () => customWebviews.filter((item) => item.webview_type !== 'intrusion'),
    [customWebviews]
  );

  const loadCustomWebviews = async () => {
    try {
      const response = await authenticatedFetch('/camera-streams/custom-webviews');
      const data = await response.json();
      setCustomWebviews(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      console.error('Failed to load custom webviews:', err);
    }
  };

  const loadAssignedCameras = async () => {
    const toOptions = (cameras, type) => (
      (Array.isArray(cameras) ? cameras : [])
        .filter((cam) => String(cam.camera_type || '').toUpperCase() === type)
        .map((cam) => ({
          id: cam.camera_id || cam.id,
          label: cam.label || cam.camera_label || cam.camera_id || cam.id,
          camera_type: type,
        }))
        .filter((cam) => cam.id)
    );
    let inOpts = [];
    let outOpts = [];
    try {
      const roi = await getROICameras();
      inOpts = toOptions(roi?.cameras, 'IN');
      outOpts = toOptions(roi?.cameras, 'OUT');
      if (!inOpts.length && !outOpts.length) {
        const streams = await getCameraStreams();
        const items = Array.isArray(streams?.items) ? streams.items : [];
        const active = items.filter((cam) => cam.is_active && (cam.camera_type === 'IN' || cam.camera_type === 'OUT'));
        inOpts = toOptions(active, 'IN');
        outOpts = toOptions(active, 'OUT');
      }
      setInCameraOptions(inOpts);
      setOutCameraOptions(outOpts);
    } catch (err) {
      console.error('Failed to load assigned cameras:', err);
    }
    return { inOpts, outOpts };
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      const startDate = format(dateRange[0].startDate, 'yyyy-MM-dd');
      const endDate = format(dateRange[0].endDate, 'yyyy-MM-dd');
      
      params.append('start_date', startDate);
      params.append('end_date', endDate);
      
      // Use authenticatedFetch which handles token refresh automatically
      const response = await authenticatedFetch(`/dashboard/analytics?${params}`);
      const data = await response.json();
      setAnalyticsData(data);
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    // Check if user is authenticated
    const token = getStoredToken();
    if (!token) {
      navigate('/login');
      return;
    }
    
    // Fetch company name from banner settings
    const fetchCompanyName = async () => {
      try {
        const response = await authenticatedFetch(`/settings/company`);
        const data = await response.json();
        if (data.company_name) {
          setCompanyName(data.company_name);
        }
      } catch (err) {
        console.error('Failed to fetch company name:', err);
      }
    };
    
    fetchCompanyName();
    fetchAnalytics();
    loadCustomWebviews();
    loadAssignedCameras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const openCreateModal = () => {
    setEditingWebview(null);
    setCustomForm(emptyCustomForm());
    setCustomError('');
    loadAssignedCameras();
    setCustomModalOpen(true);
  };

  const camerasForIds = (options, ids) => {
    const wanted = new Set((ids || []).map((id) => String(id).toLowerCase()));
    return (options || []).filter((cam) => wanted.has(String(cam.id).toLowerCase()));
  };

  const openEditModal = async (item) => {
    setEditingWebview(item);
    setCustomForm({
      name: item.name || '',
      webview_type: item.webview_type === 'intrusion' || item.webview_type === 'combined'
        ? item.webview_type
        : 'attendance',
      inCameras: camerasForIds(inCameraOptions, item.in_camera_ids),
      outCameras: camerasForIds(outCameraOptions, item.out_camera_ids),
    });
    setCustomError('');
    setCustomModalOpen(true);
    const { inOpts, outOpts } = await loadAssignedCameras();
    setCustomForm((prev) => ({
      ...prev,
      inCameras: camerasForIds(inOpts, item.in_camera_ids),
      outCameras: camerasForIds(outOpts, item.out_camera_ids),
    }));
  };

  const closeCustomModal = () => {
    if (customSaving) return;
    setCustomModalOpen(false);
    setEditingWebview(null);
    setCustomError('');
  };

  const handleSaveCustomWebview = async () => {
    const name = String(customForm.name || '').trim();
    const inIds = customForm.inCameras.map((cam) => cam.id).filter(Boolean);
    const outIds = customForm.outCameras.map((cam) => cam.id).filter(Boolean);
    if (!name) {
      setCustomError('Enter a webview name.');
      return;
    }
    if (!inIds.length && !outIds.length) {
      setCustomError('Select at least one IN or OUT camera.');
      return;
    }
    if ((customForm.webview_type === 'intrusion' || customForm.webview_type === 'combined') && !intrusionWebviewAllowed) {
      setCustomError('Intrusion webview is not available for this account.');
      return;
    }
    setCustomSaving(true);
    setCustomError('');
    const payload = {
      name,
      webview_type: customForm.webview_type,
      in_camera_ids: inIds,
      out_camera_ids: outIds,
    };
    try {
      if (editingWebview?.id) {
        await authenticatedFetch(`/camera-streams/custom-webviews/${encodeURIComponent(editingWebview.id)}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await authenticatedFetch('/camera-streams/custom-webviews', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      await loadCustomWebviews();
      setCustomModalOpen(false);
      setEditingWebview(null);
    } catch (err) {
      const detail = typeof err.detail === 'string' ? err.detail : err.message;
      setCustomError(detail || 'Failed to save custom webview.');
    } finally {
      setCustomSaving(false);
    }
  };

  const handleDeleteCustomWebview = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await authenticatedFetch(`/camera-streams/custom-webviews/${encodeURIComponent(deleteTarget.id)}`, {
        method: 'DELETE',
      });
      await loadCustomWebviews();
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete custom webview:', err);
    } finally {
      setDeleting(false);
    }
  };

  const openCustomWebview = async (item) => {
    let url = item?.webview_url;
    if (item?.id) {
      try {
        const latestRes = await authenticatedFetch(`/camera-streams/custom-webviews/${encodeURIComponent(item.id)}`);
        const latest = await latestRes.json();
        if (latest?.webview_url) {
          url = latest.webview_url;
          setCustomWebviews((prev) => prev.map((entry) => (
            entry.id === latest.id ? { ...entry, ...latest } : entry
          )));
        }
      } catch (err) {
        console.error('Failed to refresh custom webview URL:', err);
      }
    }
    if (url) {
      console.log('[CUSTOM-WEBVIEW] open', { id: item?.id, url, inIds: item?.in_camera_ids, outIds: item?.out_camera_ids });
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };



  const renderAdminDashboard = () => (
    <Box sx={{ minHeight: '100vh' }}>
      <Container 
        maxWidth="xl" 
        sx={{ 
          pt: 0,
          pb: 0,
          px: { xs: 1.5, sm: 2, md: 3, lg: 4 },
          width: '100%',
          maxWidth: '100%'
        }}
      >
        {/* Hero Header Section */}
        <Fade in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              background: '#40518A',
              borderRadius: { xs: 2, sm: 3, md: 4 },
              p: { xs: 1.5, sm: 2, md: 3 },
              mb: { xs: 2, sm: 2, md: 1 },
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)',
              width: '100%',
            }}
          >
            {/* Decorative Background Elements */}
            <Box
              sx={{
                position: 'absolute',
                top: { xs: -30, sm: -40, md: -50 },
                right: { xs: -30, sm: -40, md: -50 },
                width: { xs: 120, sm: 160, md: 200 },
                height: { xs: 120, sm: 160, md: 200 },
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                filter: 'blur(40px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: -20, sm: -25, md: -30 },
                left: { xs: -20, sm: -25, md: -30 },
                width: { xs: 100, sm: 120, md: 150 },
                height: { xs: 100, sm: 120, md: 150 },
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.08)',
                filter: 'blur(30px)',
              }}
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  mb: 0, 
                  flexWrap: 'wrap', 
                  gap: { xs: 1.5, sm: 2 },
                  width: '100%'
                }}
              >
                <Box
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: { xs: 2, sm: 3 },
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    flexShrink: 0,
                  }}
                >
                  <DashboardIcon sx={{ color: 'white', fontSize: { xs: 32, sm: 36, md: 40 } }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                  <Typography 
                    variant="h3" 
                    component="h1"
                    sx={{ 
                      color: 'white',
                      fontWeight: 500,
                      mb: { xs: 0.5, sm: 1 },
                      fontSize: { xs: '1rem', sm: '1.35rem', md: '1.5rem', lg: '1.5rem' },
                      textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                      lineHeight: 1.2,
                    }}
                  >
                    {companyName || 'Dashboard'}
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                      fontWeight: 400,
                      mt: { xs: 0.5, sm: 0 },
                    }}
                  >
                    Comprehensive overview of your Attendance system
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Fade>
        <Fade in timeout={900}>
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              border: '1px solid rgba(64, 81, 138, 0.12)',
              borderRadius: { xs: 2, sm: 3 },
              p: { xs: 1.5, sm: 2, md: 2.5 },
              mb: { xs: 2, sm: 2, md: 1 },
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 10px 28px rgba(31, 45, 84, 0.12)',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="contained"
                startIcon={<VideocamIcon />}
                onClick={() => window.open('/webview', '_blank', 'noopener,noreferrer')}
                sx={WEBVIEW_BTN_SX}
              >
                Attendance Webview
              </Button>
              {visibleCustomWebviews.map((item) => (
                <Button
                  key={item.id}
                  variant="contained"
                  startIcon={<VideocamIcon />}
                  endIcon={
                    <MoreVertIcon
                      fontSize="small"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setMenuAnchor(event.currentTarget);
                        setMenuWebview(item);
                      }}
                    />
                  }
                  onClick={() => openCustomWebview(item)}
                  sx={{
                    ...WEBVIEW_BTN_SX,
                    '& .MuiButton-endIcon': { ml: 0.5 },
                  }}
                  aria-label={item.name}
                >
                  {item.name}
                </Button>
              ))}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreateModal}
                sx={WEBVIEW_BTN_SX}
              >
                Add custom webview
              </Button>
            </Box>
          </Paper>
        </Fade>
        {/*Today's Attendance*/}
        <Grow in timeout={1000}>
          <Box
            sx={{
              width: '100%',
              gap: { xs: 1.5, sm: 2, md: 3 },
              mb: { xs: 2, sm: 2, md: 1 },
            }}
          >
            <Box
              sx={{
                transform: 'translateY(0)',
                transition: 'transform 0.3s ease-in-out',
                width: '100%',
                '&:hover': {
                  transform: { xs: 'none', sm: 'translateY(-8px)' },
                },
              }}
            >
              <AttendanceSummaryCard />
            </Box>
          </Box>
        </Grow>

        {/* Office entry traffic and  Face Recognition Statistics*/}
        <Grow in timeout={1400}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: { xs: 1.5, sm: 2, md: 1 },
              width: '100%',
              mb: { xs: 1, sm: 1 },
            }}
          >
            <Box
              sx={{
                transform: 'translateY(0)',
                transition: 'transform 0.3s ease-in-out',
                width: '100%',
                minWidth: 0,
                '&:hover': {
                  transform: { xs: 'none', sm: 'translateY(-4px)' },
                },
              }}
            >
              <LineChartCard />
            </Box>
            <Box
              sx={{
                transform: 'translateY(0)',
                transition: 'transform 0.3s ease-in-out',
                width: '100%',
                minWidth: 0,
                '&:hover': {
                  transform: { xs: 'none', sm: 'translateY(-4px)' },
                },
              }}
            >
              <PieChartCard />
            </Box>
          </Box>
        </Grow>

        {/* Match vs. Unmatch Recognitions */}
        <Grow in timeout={1200}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: { xs: 1.5, sm: 2, md: 1 },
              mb: { xs: 2, sm: 3, md: 1 },
              width: '100%',
            }}
          >
            <Box
              sx={{
                transform: 'translateY(0)',
                transition: 'transform 0.3s ease-in-out',
                width: '100%',
                '&:hover': {
                  transform: { xs: 'none', sm: 'translateY(-4px)' },
                },
              }}
            >
              <RecognitionMatchGraphCard />
            </Box>
            <Box
              sx={{
                transform: 'translateY(0)',
                transition: 'transform 0.3s ease-in-out',
                width: '100%',
                '&:hover': {
                  transform: { xs: 'none', sm: 'translateY(-4px)' },
                },
              }}
            >
              <PremisesStatsCard />
            </Box>
          </Box>
        </Grow>

        {/* User Registration Analytics and Unique Check-ins & Check-outs */}
        <Grow in timeout={1300}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: { xs: 1.5, sm: 2, md: 1 },
              mb: 0,
              width: '100%',
            }}
          >
            <Box
              sx={{
                transform: 'translateY(0)',
                transition: 'transform 0.3s ease-in-out',
                width: '100%',
                '&:hover': {
                  transform: { xs: 'none', sm: 'translateY(-4px)' },
                },
              }}
            >
              <AnalyticsGraphCard
                analyticsData={analyticsData}
                dateRange={dateRange}
                setDateRange={setDateRange}
                fetchAnalytics={fetchAnalytics}
                loading={loading}
                error={error}
                maxDate={new Date()}
              />
            </Box>
            {/* <Box
              sx={{
                transform: 'translateY(0)',
                transition: 'transform 0.3s ease-in-out',
                width: '100%',
                '&:hover': {
                  transform: { xs: 'none', sm: 'translateY(-4px)' },
                },
              }}
            >
              <UniqueCheckinCheckoutCard />
            </Box> */}
          </Box>
        </Grow>
      </Container>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuWebview(null);
        }}
      >
        <MenuItem
          onClick={() => {
            const item = menuWebview;
            setMenuAnchor(null);
            setMenuWebview(null);
            if (item) openEditModal(item);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            const item = menuWebview;
            setMenuAnchor(null);
            setMenuWebview(null);
            if (item) setDeleteTarget(item);
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={customModalOpen} onClose={closeCustomModal} fullWidth maxWidth="sm">
        <DialogTitle>{editingWebview ? 'Edit custom webview' : 'Add custom webview'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            autoFocus
            label="Webview name"
            value={customForm.name}
            onChange={(event) => setCustomForm((prev) => ({ ...prev, name: event.target.value }))}
            fullWidth
            margin="dense"
          />
          <RadioGroup
            row
            value={customForm.webview_type}
            onChange={(event) => setCustomForm((prev) => ({ ...prev, webview_type: event.target.value }))}
          >
            <FormControlLabel value="attendance" control={<Radio />} label="Attendance webview" />
            {intrusionWebviewAllowed && (
              <FormControlLabel value="combined" control={<Radio />} label="Attendance + Intrusion" />
            )}
          </RadioGroup>
          <Autocomplete
            multiple
            options={inCameraOptions}
            value={customForm.inCameras}
            onChange={(_, value) => setCustomForm((prev) => ({ ...prev, inCameras: value }))}
            getOptionLabel={cameraOptionLabel}
            isOptionEqualToValue={(option, value) => String(option.id).toLowerCase() === String(value.id).toLowerCase()}
            renderInput={(params) => <TextField {...params} label="IN camera" placeholder="Select IN cameras" />}
          />
          <Autocomplete
            multiple
            options={outCameraOptions}
            value={customForm.outCameras}
            onChange={(_, value) => setCustomForm((prev) => ({ ...prev, outCameras: value }))}
            getOptionLabel={cameraOptionLabel}
            isOptionEqualToValue={(option, value) => String(option.id).toLowerCase() === String(value.id).toLowerCase()}
            renderInput={(params) => <TextField {...params} label="OUT camera" placeholder="Select OUT cameras" />}
          />
          {customError ? <FormHelperText error>{customError}</FormHelperText> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCustomModal} disabled={customSaving}>Cancel</Button>
          <Button onClick={handleSaveCustomWebview} variant="contained" disabled={customSaving}>
            {customSaving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete custom webview</DialogTitle>
        <DialogContent>
          <Typography>
            Delete “{deleteTarget?.name}”? This removes the dashboard button. Bookmarked camera URLs are unchanged.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteCustomWebview} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

 

  // Show the admin dashboard for all users (user and admin will see the same UI)
  return (
    <Box 
      sx={{ 
        flexGrow: 1,
        minHeight: '100vh',
      }}
    >
      <DashboardWebSocketProvider>
        {renderAdminDashboard()}
      </DashboardWebSocketProvider>
    </Box>
  );
};

export default Dashboard;
